import {FC, useMemo, useState} from 'react';
import {ContentBox} from '@components/ContentBox';
import {Typography} from '@components/Typography';
import {Row} from '@components/Row';
import {Button, Col, Divider, Form, InfoTooltip, InputGroup} from '@components/index';
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl';
import {Currency} from '.cache/__types__';
import {getCurrencySymbol} from '@utils/locale';

export type MortgageProps = {
    price: number;
    currency: Currency;
    rate: number;
};

export const Mortgage: FC<MortgageProps> = ({price, currency, rate}) => {
    const intl = useIntl();
    const [value, setValue] = useState<number>(Math.round((0.75 * price) / 100) * 100);
    const [years, setYears] = useState<number>(30);

    const currencySymbol = useMemo(() => getCurrencySymbol(intl, currency), [intl, currency]);

    const payment = Math.round((value * (rate / 12)) / (1 - 1 / Math.pow(1 + rate / 12, years * 12)));

    return (
        <ContentBox className="mb-8 mb-lg-20 px-5 py-6 px-xl-8 py-xl-10">
            <Typography as="h2" variant="subheadline" className="mb-3 mb-lg-8">
                <FormattedMessage defaultMessage={'Bezrealitky Hypotéka'} />
            </Typography>
            <Row className="g-5 mb-5">
                <Col lg={8}>
                    <Form.Label>
                        <FormattedMessage defaultMessage={'Hodnota nemovitosti'} />
                    </Form.Label>

                    <InputGroup>
                        <Form.Control disabled value={intl.formatNumber(price, {style: 'currency', currency, maximumFractionDigits: 0})} />
                    </InputGroup>
                </Col>

                <Col lg={8}>
                    <Form.Label htmlFor="mortgage">
                        <FormattedMessage defaultMessage={'Výše úvěru'} />
                    </Form.Label>
                    <InputGroup>
                        <Form.Control
                            type={"number"}
                            value={value}
                            onChange={(e) => setValue(parseInt(e.currentTarget.value))}
                            placeholder={intl.formatMessage({defaultMessage: 'Výše úvěru'})}
                            id="mortgage"
                        />
                        <InputGroup.Text>{currencySymbol}</InputGroup.Text>
                    </InputGroup>
                </Col>

                <Col lg={8}>
                    <Form.Label htmlFor="own-resources">
                        <FormattedMessage defaultMessage={'Vlastní zdroje'} />
                    </Form.Label>

                    <InputGroup>
                        <Form.Control
                            type={"number"}
                            value={price - value}
                            onChange={(e) => setValue(price - parseInt(e.currentTarget.value))}
                            placeholder={intl.formatMessage({defaultMessage: 'Vlastní zdroje'})}
                            id="own-resources"
                        />
                        <InputGroup.Text>{currencySymbol}</InputGroup.Text>
                    </InputGroup>
                </Col>

                <Col lg={8}>
                    <Form.Label htmlFor="mortgage-length">
                        <InfoTooltip content="Informace o době splácení">
                            <FormattedMessage defaultMessage={'Doba splácení'} />
                        </InfoTooltip>
                    </Form.Label>

                    <InputGroup>
                        <Form.Control
                            type={"number"}
                            value={years}
                            onChange={(e) => setYears(parseInt(e.currentTarget.value))}
                            placeholder={intl.formatMessage({defaultMessage: 'Doba splácení'})}
                            id="mortgage-length"
                        />
                        <InputGroup.Text>
                            <FormattedMessage defaultMessage={'let'} />
                        </InputGroup.Text>
                    </InputGroup>
                </Col>
            </Row>
            <Divider className="my-8 d-none d-lg-block" />
            <Typography textAlign="center" as="p" className="mb-4 mb-lg-8">
                {!Number.isNaN(payment) &&
                    <FormattedMessage
                        defaultMessage={
                            'Tato nemovitost již od <bold>{price}</bold> měsíčně s roční úrokovou sazbou <bold>{rate}</bold>.'
                        }
                        values={{
                            bold: (chunks: any) => (
                                <Typography variant="perex" fontWeight="bold">
                                    {chunks}
                                </Typography>
                            ),
                            price: <FormattedNumber value={payment} style={'currency'} currency={currency}
                                                    maximumFractionDigits={0}/>,
                            rate: <FormattedNumber value={rate} style={"percent"} maximumFractionDigits={2} />,
                        }}
                    />
                }

                {Number.isNaN(payment) &&
                    <FormattedMessage defaultMessage={"Pro výpočet vyplňte všechna pole"} />
                }
            </Typography>

            <Row className="justify-content-lg-center">
                <Col xs={12} lg="auto">
                    <Button href="https://hypoteka.bezrealitky.cz/" className="w-100">
                        <FormattedMessage defaultMessage={'Zjistit více informací'} />
                    </Button>
                </Col>
            </Row>
        </ContentBox>
    );
};
